import React, { useState } from 'react';
import { ExpandMore, UnfoldMore } from '@mui/icons-material';
import { Journals } from '../../../types/Journals.types';
import { useTranslation } from 'react-i18next';
import { FilterPopover } from './FilterPopover';
import { InternalSearchFilter, InternalSearchFilterKeys } from '../types';
import {
  Chip,
  Table,
  TableContainer,
  TableHead,
  TableCell,
  Link as MuiLink,
  TableRow,
  TableBody,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { mapAgreementStatus } from '../util/agreement-status';
import { SortableHeaderButton } from '../../../components/TableHeaderButton';

const arrowStylingDefault = {
  transition: 'transform 150ms ease',
};

const arrowStyling = {
  transform: 'rotate(-180deg)',
  transition: 'transform 150ms ease',
};

interface JournalsTableDesktopProps {
  journals: Journals[] | undefined;
  order: 'asc' | 'desc';
  orderBy: keyof Journals;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Journals
  ) => void;
  filters: InternalSearchFilter;
  selectedFilters: InternalSearchFilter;
  onToggleFilter: (type: InternalSearchFilterKeys, value: string) => void;
}

export const JournalsTableDesktop = ({
  journals,
  order,
  orderBy,
  onRequestSort,
  filters,
  selectedFilters,
  onToggleFilter,
}: JournalsTableDesktopProps) => {
  const { t } = useTranslation();

  const [openFilter, setOpenFilter] = useState<{
    key: InternalSearchFilterKeys;
    anchor: HTMLElement;
  } | null>(null);

  const getArrowIcon = (key: keyof Journals) => {
    if (orderBy === key) {
      return order === 'asc' ? (
        <ExpandMore fontSize="small" style={arrowStyling} />
      ) : (
        <ExpandMore fontSize="small" style={arrowStylingDefault} />
      );
    }
    return <UnfoldMore fontSize="small" style={arrowStylingDefault} />;
  };

  return (
    <TableContainer
      sx={{
        mt: '24px',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          width: '100%',
        }}
        aria-label="Journals list"
      >
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <SortableHeaderButton
                variant="secondary"
                onClick={(event) => onRequestSort(event, 'name')}
                isSorted={orderBy === 'name'}
                sortDirection={order}
              >
                {t('agreements.journal')}
                {getArrowIcon('name')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell align="left">
              <FilterPopover
                label={t('agreements.imprint')}
                options={filters.imprints}
                selectedOptions={selectedFilters.imprints}
                onToggle={(imprint) => onToggleFilter('imprints', imprint)}
                anchorEl={
                  openFilter?.key === 'imprints' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'imprints'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'imprints', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell align="left">
              <FilterPopover
                label={t('agreements.createFlow.payment')}
                options={filters.payments}
                selectedOptions={selectedFilters.payments}
                onToggle={(payment) => onToggleFilter('payments', payment)}
                anchorEl={
                  openFilter?.key === 'payments' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'payments'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'payments', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell align="left">
              <FilterPopover
                label={t('agreements.licenseOption')}
                options={filters.licenses}
                selectedOptions={selectedFilters.licenses}
                onToggle={(license) => onToggleFilter('licenses', license)}
                anchorEl={
                  openFilter?.key === 'licenses' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'licenses'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'licenses', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell align="left">
              <FilterPopover
                label={t('agreements.publishingModel')}
                options={filters.publishingModels}
                selectedOptions={selectedFilters.publishingModels}
                onToggle={(model) => onToggleFilter('publishingModels', model)}
                anchorEl={
                  openFilter?.key === 'publishingModels'
                    ? openFilter.anchor
                    : null
                }
                open={openFilter?.key === 'publishingModels'}
                onOpen={(e) =>
                  setOpenFilter({
                    key: 'publishingModels',
                    anchor: e.currentTarget,
                  })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell align="left">
              <SortableHeaderButton
                variant="secondary"
                onClick={(event) => onRequestSort(event, 'issn_e')}
                isSorted={orderBy === 'issn_e'}
                sortDirection={order}
              >
                {t('agreements.issne')}
                {getArrowIcon('issn_e')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell align="left">
              <SortableHeaderButton
                variant="secondary"
                onClick={(event) => onRequestSort(event, 'issn_p')}
                isSorted={orderBy === 'issn_p'}
                sortDirection={order}
              >
                {t('agreements.issnp')}
                {getArrowIcon('issn_p')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell align="left">
              <SortableHeaderButton
                variant="secondary"
                onClick={(event) => onRequestSort(event, 'subjects')}
                isSorted={orderBy === 'subjects'}
                sortDirection={order}
              >
                {t('agreements.subject')}
                {getArrowIcon('subjects')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell align="left">
              <SortableHeaderButton
                variant="secondary"
                onClick={(event) => onRequestSort(event, 'agreement_name')}
                isSorted={orderBy === 'agreement_name'}
                sortDirection={order}
              >
                {t('agreements.agreement')}
                {getArrowIcon('agreement_name')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell
              align="center"
              sx={{
                '& > div': {
                  justifyContent: 'center',
                },
              }}
            >
              <FilterPopover
                label={t('agreements.status')}
                options={filters.statuses}
                selectedOptions={selectedFilters.statuses}
                onToggle={(status) => onToggleFilter('statuses', status)}
                anchorEl={
                  openFilter?.key === 'statuses' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'statuses'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'statuses', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
                optionLabelMapper={mapAgreementStatus}
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {journals?.map((journal, index) => (
            <TableRow
              key={
                journal._id ||
                `journal-table-row-${journal.url}-${journal.name}-${
                  journal.agreement_id || index
                }`
              }
            >
              <TableCell>
                <MuiLink
                  href={journal.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {journal.name}
                </MuiLink>
              </TableCell>
              <TableCell>{journal.imprint}</TableCell>
              <TableCell>{journal.payment}</TableCell>
              <TableCell>{journal.licenses?.join(', ')}</TableCell>
              <TableCell>{journal.publishing_model}</TableCell>
              <TableCell>{journal.issn_e}</TableCell>
              <TableCell>{journal.issn_p}</TableCell>
              <TableCell>{journal.subjects}</TableCell>
              <TableCell>
                <MuiLink
                  component={RouterLink}
                  to={`/agreements/${journal.agreement_id}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {journal.agreement_name}
                </MuiLink>
              </TableCell>
              <TableCell align="center">
                {journal.status ? (
                  <Chip
                    size="small"
                    variant={
                      journal.status === 'enabled' ? 'success' : 'secondary'
                    }
                    label={mapAgreementStatus(journal.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                ) : (
                  '-'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
